import React, { useState, useContext, useEffect } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, btnSec, rad, sp, icBox } from "../../constants/styles";
import MC from "../common/MC";
import { can } from "../../utils/permissions";
import Ic from "../common/Ic";
import { isExpiringSoon, getDaysRemaining, getLeaseForProperty, isOverdue } from "../../utils/leaseHelpers";
import FitBadge from "../common/FitBadge";
import { getPropertyFit } from "../../utils/propertyFit";
import { getPlanUsage } from "../../utils/plans";
import UpgradeModal from "../common/UpgradeModal";
import RevenueChart from "./RevenueChart";

const COM_TYPES = ["office", "shop", "showroom", "warehouse", "clinic", "coworking", "industrial"];
const isCommercial = (pt) => COM_TYPES.includes(pt);
const ptLabel = (pt, t) => {
  const m = { apartment: t.ptApartment, independent_house: t.ptIndependentHouse, villa: t.ptVilla, pg_room: t.ptPgRoom, office: t.ptOffice, shop: t.ptShop, showroom: t.ptShowroom, warehouse: t.ptWarehouse, clinic: t.ptClinic, coworking: t.ptCoworking, industrial: t.ptIndustrial };
  return m[pt] || pt;
};

function LandlordDash() {
  const { t, h, props, setProps, tens, setTens, pays, setPays, mts, go, setSelPropId, flash, addNotif, showConfirm, leases, setLeases, role, plan, refreshData, userName, orgRole } = useContext(Ctx);
  const usage = getPlanUsage(plan.type, props.length, plan.maxProperties);
  const planFeatures = plan.features || [];
  const isFree = plan.type === "free";
  // Map quick action pages to required features for lock badge
  const quickActionFeatures = {
    agreement: "leases",
    servicePersons: "service_persons",
    reports: "reports",
  };
  const isFeatureLocked = (pg) => {
    const feat = quickActionFeatures[pg];
    return feat && !planFeatures.includes(feat);
  };
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [hotLeadCount, setHotLeadCount] = useState(0);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [todayVisits, setTodayVisits] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const [followUpCount, setFollowUpCount] = useState(0);
  const due = pays.filter(p => p.st === "due").reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const pendingPays = pays.filter(p => p.pending);
  const partialPays = pays.filter(p => p.st === "partial");
  const recentRzpPays = pays.filter(p => {
    const txns = p.txns || [];
    const today = new Date().toISOString().slice(0, 10);
    return txns.some(tx => tx.mth === "razorpay" && tx.status === "verified" && tx.dt === today);
  });
  const occ = props.filter(p => p.occ).length;
  const [showQuick, setShowQuick] = useState(false);
  const overduePays = pays.filter(p => (p.st === "due" || p.st === "partial") && !p.pending && isOverdue(p.mo));
  const overdueTotal = overduePays.reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const openMaintItems = mts.filter(m => m.st === "open" || m.st === "assigned");
  const collectedTotal = pays.filter(p => p.st === "paid").reduce((s, p) => s + p.amt + (p.com || 0), 0);
  const activeLeaseCount = leases.filter(l => l.status === "active").length;

  useEffect(() => {
    const autoGen = async () => {
      const res = await api.generateDuePayments();
      if (res.success && res.generated > 0) {
        flash(`${t.generateMsg} ${res.generated} ${t.newRentBills} ${res.month}`);
        const data = await api.sync();
        if (data && data.pays) setPays(data.pays);
      }
    };
    autoGen();
  }, []);

  // Phase 16: Fetch new lead count + Phase 18: visit data
  useEffect(() => {
    (async () => {
      const data = await api.getLeads();
      if (data.success) {
        const leads = data.leads;
        setNewLeadCount(leads.filter(l => l.status === "new").length);
        setHotLeadCount(leads.filter(l => {
          if (l.status !== "new") return false;
          const hrs = (Date.now() - new Date(l.created_at).getTime()) / (1000 * 60 * 60);
          const hasSignal = (l.message && l.message.trim()) || l.pref_rooms || l.pref_max_rent;
          return hrs < 72 || hasSignal;
        }).length);
        // Phase 18: Visit tracking
        const today = new Date().toISOString().split('T')[0];
        setTodayVisits(leads.filter(l => l.visit_date === today && l.visit_status === "scheduled"));
        setUpcomingVisits(leads.filter(l => l.visit_date > today && l.visit_status === "scheduled"));
        // Phase 19: Follow-up alerts
        setFollowUpCount(leads.filter(l => l.follow_up_sent === 1 && l.status === "new").length);
      }
    })();
  }, []);

  const expiringLeases = leases.filter(l => isExpiringSoon(l));
  const vacReqs = tens.filter(t => t.vac);
  const attentionCount = overduePays.length + pendingPays.length + partialPays.length + recentRzpPays.length + expiringLeases.length + openMaintItems.length + vacReqs.length + newLeadCount + followUpCount;
  const approveVacate = (te) => {
    showConfirm(`${t.approveVacFor} ${te.name}?`, () => {
      api.approveVacate(te.id, te.pid);
      setTens(tens.map(t => t.id === te.id ? { ...t, vac: false, pid: null } : t));
      setProps(props.map(p => p.id === te.pid ? { ...p, occ: false, tid: null } : p));
      setLeases(leases.map(l => l.tenant_id === te.id && (l.status === "active" || l.status === "draft") ? { ...l, status: "terminated" } : l));
      addNotif("landlord", null, `Approved vacate for ${te.name}`, "info");
      addNotif("tenant", te.id, t.vacApprovedMsg, "info");
      flash(t.vacApproved);
    });
  };
  const rejectVacate = (te) => {
    showConfirm(`${t.rejectVacFor} ${te.name}?`, () => {
      api.rejectVacate(te.id);
      setTens(tens.map(t => t.id === te.id ? { ...t, vac: false } : t));
      addNotif("landlord", null, `Rejected vacate for ${te.name}`, "info");
      addNotif("tenant", te.id, t.vacRejectedMsg, "info");
      flash(t.vacRejected);
    });
  };

  // Attention item renderer
  const AttItem = ({ icon, color, title, sub, onClick, last }) => (
    <div onClick={onClick} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.md}px ${sp.lg}px`, borderBottom: last ? "none" : `1px solid ${h.bd}`, cursor: "pointer", transition: "background 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={icBox(color, 36)}><span style={{ fontSize: 15 }}>{icon}</span></div>
        <div><div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{title}</div>
          <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 1 }}>{sub}</div></div>
      </div>
      <Ic n="back" s={14} c={h.tt || h.sc} style={{ transform: "rotate(180deg)" }} />
    </div>
  );

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    {/* Welcome */}
    <h2 style={{ color: h.tx, fontSize: 22, fontWeight: 700, marginBottom: sp.xl, letterSpacing: "-0.3px" }}>{t.welcome}{userName ? `, ${userName}` : ""}!</h2>

    {/* Needs Attention */}
    {attentionCount > 0 ? <div style={{ marginBottom: sp.xxl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
        <h3 style={{ color: h.tx, fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: "-0.1px" }}>{t.needsAttention}</h3>
        <span style={{ background: `${h.rd}12`, color: h.rd, padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700 }}>{attentionCount}</span>
      </div>
      <div style={{ background: h.cd, borderRadius: rad.lg, overflow: "hidden", border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {overduePays.length > 0 && <AttItem icon="!!" color={h.rd} title={t.overdueRents} sub={`${overduePays.length} ${t.tenants.toLowerCase()} \u00B7 \u20B9${overdueTotal.toLocaleString()}`} onClick={() => go("pay")} />}
        {pendingPays.length > 0 && <AttItem icon="\u23F3" color={h.wn} title={t.pendingApprovals} sub={`${pendingPays.length} ${t.payments.toLowerCase()}`} onClick={() => go("pay")} />}
        {recentRzpPays.length > 0 && <AttItem icon="💳" color="#16a34a" title="New Razorpay Payment" sub={`${recentRzpPays.length} payment(s) received today`} onClick={() => go("pay")} />}
        {partialPays.length > 0 && <AttItem icon="🟡" color={h.wn} title="Partial Payments" sub={`${partialPays.length} tenant(s) paid partially`} onClick={() => go("pay")} />}
        {expiringLeases.length > 0 && <AttItem icon={"\uD83D\uDCCB"} color={h.wn} title={t.expiringLeases} sub={`${expiringLeases.length} \u2014 ${Math.min(...expiringLeases.map(l => getDaysRemaining(l)))} ${t.days}`} onClick={() => go("agreement")} />}
        {openMaintItems.length > 0 && (() => { const highCount = openMaintItems.filter(m => m.urg === "high").length; return <AttItem icon={"\uD83D\uDD27"} color={h.rd} title={t.openMaint} sub={`${highCount > 0 ? highCount + " " + t.high.toLowerCase() + " \u00B7 " : ""}${openMaintItems.length} ${t.total.toLowerCase()}`} onClick={() => go("maint")} />; })()}
        {vacReqs.length > 0 && <div style={{ padding: `${sp.md}px ${sp.lg}px` }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.md }}>
            <div style={icBox(h.rd, 36)}><span style={{ fontSize: 15 }}>{"\uD83C\uDFE0"}</span></div>
            <div><div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{t.vacateRequests}</div>
              <div style={{ color: h.ts || h.sc, fontSize: 11 }}>{vacReqs.length} {t.tenants.toLowerCase()}</div></div>
          </div>
          {vacReqs.map(te => {
            const p = props.find(x => x.id === te.pid);
            return <div key={te.id} style={{ background: h.sf || h.bg, borderRadius: rad.md, padding: sp.md, marginBottom: sp.xs + 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: h.wn, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>{te.name[0]}</div>
                <div><div style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{te.name}</div><div style={{ color: h.ts || h.sc, fontSize: 10 }}>{p?.addr || t.noProperty}</div></div>
              </div>
              <div style={{ display: "flex", gap: sp.sm }}>
                <button onClick={() => approveVacate(te)} style={{ ...btnP(h), background: h.gn, flex: 1, fontSize: 12, padding: 10, boxShadow: `0 2px 8px ${h.gn}25` }}>{"\u2713 " + t.approve}</button>
                <button onClick={() => rejectVacate(te)} style={{ ...btnSec(h), color: h.rd, flex: 1, fontSize: 12, padding: 10 }}>{"\u2715 " + t.reject}</button>
              </div>
            </div>;
          })}
        </div>}
        {newLeadCount > 0 && <AttItem icon={"\uD83D\uDCE9"} color={h.ac} title={`${hotLeadCount > 0 ? ("\uD83D\uDD25 " + hotLeadCount + " " + (t.hotLeads || "Hot") + " \u00B7 ") : ""}${newLeadCount} ${t.newLeads || "New Leads"}`} sub={t.tapToReview || "Tap to review"} onClick={() => go("leads")} />}
        {followUpCount > 0 && <AttItem icon={"\uD83D\uDD14"} color={h.wn} title={`${followUpCount} ${t.followUpNeeded || "Need Follow-up"}`} sub={t.followUpDesc || "Auto-reminded, still uncontacted"} onClick={() => go("leads")} last />}
      </div>
    </div> : <div style={{ ...crd(h), textAlign: "center", padding: sp.xl, marginBottom: sp.xxl }}>
      <div style={{ color: h.gn, fontSize: 14, fontWeight: 600 }}>{"\u2713 " + t.noAttention}</div>
    </div>}

    {/* Phase 18: Today's Visits */}
    {todayVisits.length > 0 && <div style={{ marginBottom: sp.xl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.md }}>
        <h3 style={{ color: h.tx, fontSize: 14, fontWeight: 700, margin: 0 }}>{t.todaysVisits || "Today's Visits"}</h3>
        <span style={{ background: `${h.ac}12`, color: h.ac, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{todayVisits.length}</span>
      </div>
      <div style={{ background: h.cd, borderRadius: rad.lg, overflow: "hidden", border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {todayVisits.map((v, i) => <div key={v.id} onClick={() => go("leads")} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.md}px ${sp.lg}px`,
          borderBottom: i < todayVisits.length - 1 ? `1px solid ${h.bd}` : "none", cursor: "pointer"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
            <div style={{ ...icBox(h.ac, 36), fontWeight: 700, fontSize: 11, color: h.ac }}>{v.visit_time}</div>
            <div>
              <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{v.name}</div>
              <div style={{ color: h.ts || h.sc, fontSize: 11 }}>{v.property_addr || v.phone}</div>
            </div>
          </div>
          <Ic n="back" s={14} c={h.tt || h.sc} />
        </div>)}
      </div>
    </div>}

    {/* Phase 18: Upcoming Visits */}
    {upcomingVisits.length > 0 && <div style={{ marginBottom: sp.xl }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.sm }}>
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{t.upcomingVisits || "Upcoming Visits"}</span>
        <span style={{ color: h.ts || h.sc, fontSize: 11 }}>({upcomingVisits.length})</span>
      </div>
      <div style={{ background: h.cd, borderRadius: rad.md, padding: `${sp.sm}px ${sp.lg}px`, border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {upcomingVisits.slice(0, 3).map(v => <div key={v.id} onClick={() => go("leads")} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", cursor: "pointer" }}>
          <span style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{v.name}</span>
          <span style={{ color: h.ts || h.sc, fontSize: 11 }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {"\u00B7"} {v.visit_time}</span>
        </div>)}
        {upcomingVisits.length > 3 && <div style={{ color: h.ac, fontSize: 11, marginTop: sp.xs, textAlign: "center", fontWeight: 500 }}>+{upcomingVisits.length - 3} {t.more || "more"}</div>}
      </div>
    </div>}

    {/* Metrics Grid */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.md, marginBottom: sp.xxl }}>
      <MC ic="dollar" l={t.collected} v={`\u20B9${collectedTotal.toLocaleString()}`} cl={h.gn} h={h} onClick={() => go("pay")} />
      <MC ic="dollar" l={t.outstanding} v={`\u20B9${due.toLocaleString()}`} cl={due > 0 ? h.rd : h.gn} h={h} onClick={() => go("pay")} />
      <MC ic="bldg" l={t.properties} v={`${occ}/${props.length}`} cl={h.ac} h={h} onClick={() => go("props")} />
      <MC ic="file" l={t.agreements} v={planFeatures.includes("leases") ? activeLeaseCount : "\uD83D\uDD12"} cl={planFeatures.includes("leases") ? h.gn : (h.tt || h.sc)} h={h} onClick={() => go("agreement")} />
    </div>

    {/* Revenue Trend Chart */}
    <RevenueChart pays={pays} h={h} t={t} onClick={() => go("reports")} />

    {/* Plan usage strip — owner only */}
    {isFree && can(orgRole, "upgrade") && <div style={{ display: "flex", alignItems: "center", gap: sp.md, background: h.cd, borderRadius: rad.md, padding: `${sp.md}px ${sp.lg}px`, marginBottom: sp.xxl, border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
      <div style={icBox(h.ac, 32)}><span style={{ fontSize: 13 }}>{"\uD83D\uDCE6"}</span></div>
      <div style={{ flex: 1 }}>
        <div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{t.planFreeLabel || "Free Plan"}: {usage.used}/{usage.limit} {t.properties.toLowerCase()}</div>
        <div style={{ background: h.ib, borderRadius: 3, height: 4, marginTop: 4, overflow: "hidden" }}>
          <div style={{ background: usage.atLimit ? h.wn : h.ac, height: "100%", width: `${usage.pct}%`, borderRadius: 3, transition: "width 0.5s ease" }} />
        </div>
      </div>
      <button onClick={() => setShowUpgrade(true)} style={{ background: h.pp || h.ac, color: "#fff", border: "none", borderRadius: rad.sm, padding: `${sp.xs + 2}px ${sp.lg - 2}px`, fontSize: 10, fontWeight: 600, cursor: "pointer", boxShadow: `0 2px 8px ${h.pp || h.ac}30`, whiteSpace: "nowrap" }}>{usage.atLimit ? (t.planUpgrade || "Upgrade") : (t.planUpgrade || "Upgrade")}</button>
    </div>}

    {/* Quick Actions */}
    <div style={{ position: "relative", marginBottom: sp.xxl }}>
      <button onClick={() => setShowQuick(!showQuick)} style={{ background: h.cd, border: `1px solid ${h.bd}`, borderRadius: rad.md, padding: `${sp.md}px ${sp.lg}px`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", boxShadow: h.sh }}>
        <span style={{ display: "flex", alignItems: "center", gap: sp.sm }}><Ic n="zap" s={16} c={h.ac} /><span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{t.quick}</span></span>
        <span style={{ color: h.tt || h.sc, fontSize: 14, transform: showQuick ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}>{"\u25BE"}</span>
      </button>
      {showQuick && <div className="animate-fadeIn" style={{ background: h.cd, borderRadius: rad.md, marginTop: sp.xs, overflow: "hidden", border: `1px solid ${h.bd}`, boxShadow: h.sh }}>
        {[[t.addProp, "plus", "addProp"], [t.leads || "Leads", "users", "leads"], [t.invite, "users", "inviteTenant"], [t.mapView, "bldg", "mapView"], [t.reports || "Reports", "file-text", "reports"], [t.settings || "Settings", "gear", "settings"], [t.whatsappSettings || "WhatsApp", "message-circle", "paySettings"], [t.agreements, "file", "agreement"], [t.servicePersons, "wrench", "servicePersons"]].map(([l, ic, pg], i) => {
          const locked = isFeatureLocked(pg);
          return <button key={i} onClick={() => { go(pg); setShowQuick(false); }} style={{ background: "transparent", border: "none", borderBottom: `1px solid ${h.bd}`, padding: `${sp.md}px ${sp.lg}px`, cursor: "pointer", display: "flex", alignItems: "center", gap: sp.md, width: "100%", transition: "background 0.15s", opacity: locked ? 0.6 : 1 }}>
            <div style={icBox(locked ? (h.tt || h.sc) : h.ac, 30)}><Ic n={ic} s={14} c={locked ? (h.tt || h.sc) : h.ac} /></div>
            <span style={{ color: locked ? (h.ts || h.sc) : h.tx, fontSize: 13, fontWeight: 500 }}>{l}</span>
            {locked && <span style={{ marginLeft: "auto", background: `${h.wn}15`, color: h.wn, padding: "2px 8px", borderRadius: rad.pill, fontSize: 9, fontWeight: 700, letterSpacing: "0.3px" }}>{"\uD83D\uDD12"} Upgrade</span>}
          </button>;
        })}
      </div>}
    </div>

    {/* Properties List */}
    <h3 style={{ color: h.tx, fontSize: 15, fontWeight: 700, marginBottom: sp.md, letterSpacing: "-0.1px" }}>{t.properties}</h3>
    {props.map(p => {
      const te = tens.find(x => x.id === p.tid);
      const pLease = getLeaseForProperty(leases, p.id);
      const pLeaseDays = pLease ? getDaysRemaining(pLease) : null;
      return <div key={p.id} onClick={() => { setSelPropId(p.id); go("propDetail"); }} style={{ ...crd(h), cursor: "pointer" }} onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = h.s2 || h.sh; }} onMouseOut={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = h.sh; }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}><div style={{ flex: 1 }}><div style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{p.addr}</div>
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
            {p.property_type && p.property_type !== "apartment" && <span style={{ display: "inline-block", background: isCommercial(p.property_type) ? `${h.gn}10` : `${h.ac}10`, color: isCommercial(p.property_type) ? h.gn : h.ac, padding: "2px 8px", borderRadius: rad.pill, fontSize: 10, fontWeight: 600 }}>{ptLabel(p.property_type, t)}</span>}
            <FitBadge fit={getPropertyFit(p, { leases, role, t, h })} h={h} />
          </div>
          <div style={{ color: h.ts || h.sc, fontSize: 12, marginTop: 4 }}>{p.rooms} {t.rooms} {"\u00B7"} {"\u20B9"}{p.rent.toLocaleString()}/mo</div></div>
          <span style={bdg(p.occ ? `${h.gn}12` : `${h.wn}12`, p.occ ? h.gn : h.wn)}>{p.occ ? t.occupied : t.vacant}</span></div>
        {pLease && pLease.end_date && <div style={{ marginTop: sp.sm, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: pLeaseDays !== null && pLeaseDays <= 30 ? h.wn : h.ts || h.sc, background: pLeaseDays !== null && pLeaseDays <= 30 ? `${h.wn}10` : `${(h.ts || h.sc)}10`, padding: "3px 10px", borderRadius: rad.pill, fontWeight: 500 }}>{t.leaseExpiry}: {pLease.end_date}{pLeaseDays !== null && pLeaseDays <= 30 ? ` (${pLeaseDays}d)` : ""}</span></div>}
        {te && <div style={{ marginTop: sp.sm + 2, display: "flex", alignItems: "center", gap: sp.sm, paddingTop: sp.sm + 2, borderTop: `1px solid ${h.bd}` }}>
          <div style={{ width: 28, height: 28, borderRadius: 14, background: `${h.ac}15`, display: "flex", alignItems: "center", justifyContent: "center", color: h.ac, fontSize: 12, fontWeight: 700 }}>{te.name[0]}</div>
          <div><div style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{te.name}</div><div style={{ color: h.tt || h.sc, fontSize: 10 }}>{te.ph}</div></div>
          {te.vac && <span style={{ marginLeft: "auto", ...bdg(`${h.rd}12`, h.rd) }}>{t.vacate}</span>}</div>}
      </div>;
    })}
    {showUpgrade && <UpgradeModal h={h} t={t} used={usage.used} limit={usage.limit} currentPlan={plan.type} onClose={() => setShowUpgrade(false)} onUpgradeSuccess={async () => { if (refreshData) await refreshData(); flash(t.billingSuccess || "Plan Upgraded!"); setShowUpgrade(false); }} />}
  </div>;
}

export default LandlordDash;
